/**
 * Emergency Audit Log Component (AC-009)
 *
 * Displays a chronological list of all emergency data access events.
 * Patients can see who accessed their data and mark entries as "acknowledged".
 */

import { Clock, Eye, Globe, RefreshCw, ShieldAlert, User } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { useI18nStore } from "../../stores/i18nStore";
import {
  AccessLogEntry,
  emergencyService,
} from "../../services/emergency.service";

const EmergencyAuditLog: React.FC = () => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const [logs, setLogs] = useState<AccessLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<AccessLogEntry | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const data = await emergencyService.getAccessLogs(30);
      setLogs(data);
    } catch {
      // empty list is fine
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const formatDate = (iso?: string) => {
    if (!iso) return "–";
    return fmt.dateTime(iso, "medium");
  };

  const severityBadge = (entry: AccessLogEntry) => {
    if (entry.revokedAt)
      return (
        <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700 font-medium">
          {t.emergxRevoked}
        </span>
      );
    if (new Date(entry.expiresAt || 0) < new Date())
      return (
        <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
          {t.expired}
        </span>
      );
    return (
      <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
        {t.active}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-400">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" /> {t.loading}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-orange-600" />
          <h3 className="font-semibold text-gray-900">{t.emergxAuditTitle}</h3>
        </div>
        <button
          onClick={load}
          className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
        >
          <RefreshCw className="w-3.5 h-3.5" /> {t.refresh}
        </button>
      </div>

      {logs.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Eye className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">{t.emergxAuditEmpty}</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {logs.map((log) => (
            <button
              key={log.id}
              className="w-full text-left py-3 cursor-pointer hover:bg-gray-50 rounded-lg px-2 transition-colors"
              onClick={() => setSelected(selected?.id === log.id ? null : log)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <User className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {log.responderName}
                      {log.responderOrganization && (
                        <span className="font-normal text-gray-500">
                          {" "}
                          · {log.responderOrganization}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                      <Clock className="w-3 h-3" />
                      {formatDate(log.grantedAt)}
                      {log.ipAddress && (
                        <>
                          <Globe className="w-3 h-3 ml-2" />
                          {log.ipAddress}
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="shrink-0">{severityBadge(log)}</div>
              </div>

              {/* Expanded detail */}
              {selected?.id === log.id && (
                <div className="mt-3 ml-7 space-y-1 text-xs text-gray-600 bg-gray-50 rounded-lg p-3">
                  <p>
                    <strong>{t.emergxAuditAccessType}</strong> {log.accessType}
                  </p>
                  {log.accessReason && (
                    <p>
                      <strong>{t.emergxAuditReason}</strong> {log.accessReason}
                    </p>
                  )}
                  {log.responderEmail && (
                    <p>
                      <strong>{t.emergxAuditEmail}</strong> {log.responderEmail}
                    </p>
                  )}
                  {log.expiresAt && (
                    <p>
                      <strong>{t.emergxAuditTokenExpired}</strong>{" "}
                      {formatDate(log.expiresAt)}
                    </p>
                  )}
                  {log.revokedAt && (
                    <p>
                      <strong>{t.emergxAuditRevokedAt}</strong>{" "}
                      {formatDate(log.revokedAt)}
                    </p>
                  )}
                  <p>
                    <strong>{t.emergxAuditConsent}</strong>{" "}
                    {log.consentDocumented ? (
                      <span className="text-green-700">{t.emergxYes}</span>
                    ) : (
                      <span className="text-orange-600">
                        {t.emergxAuditImpliedConsent}
                      </span>
                    )}
                  </p>
                  {Array.isArray(log.accessedResources) &&
                    log.accessedResources.length > 0 && (
                      <p>
                        <strong>{t.emergxAuditResources}</strong>{" "}
                        {log.accessedResources.join(", ")}
                      </p>
                    )}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmergencyAuditLog;
